import React, { useMemo } from 'react';
import classNames from 'classnames';
import { Icon } from '../Icon/Icon';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import uuidv4 from "../../utils/uuidv4";
import { Heading } from "../Heading/Heading";
/**
 * Bronson TileSelect component.
 * @see https://bronson.vwfs.io/default/components/detail/bronson-tile-select.html
 * @constructor
 */
export function TileSelect({ checkboxGroupName, checked, children, defaultChecked, disabled, errorMessage, footer, headingLevel, icon, id, inputProps, inputType = 'checkbox', onChange, onClick, radioGroupName, title, value, testId, ...otherProps }) {
    /**
     * Generate unique id, if prop `id` is not provided to improve accessibility.
     */
    const tileSelectId = useMemo(() => id ?? uuidv4('tile-select-'), [id]);
    const iconClassNameList = classNames('c-tile-select__icon');
    /**
     * Set the `[name]` value depending on the {@link inputType}.
     * If the type is `radio`, then use `radioGroupName` to ensure that all
     * input elements have the same value to be included in the radio group.
     * In the case of `checkbox`, use the {@link checkboxGroupName} if set to
     * group the TileSelects, otherwise the {@link id} is used.
     * @type {string}
     */
    const inputName = inputType === 'radio' ? radioGroupName : (checkboxGroupName ?? tileSelectId);
    /**
     * Callback when user clicks on the {@link TileSelect} element.
     * @type {function}
     */
    const handleClick = (e) => {
        if (onClick) {
            onClick(e);
        }
    };
    /**
     * Callback when state of the {@link TileSelect} element has changed.
     * @type {function}
     */
    const handleChange = (e) => {
        if (onChange) {
            onChange(e);
        }
    };
    const renderIfFooter = () => {
        if (footer) {
            return React.createElement("span", { className: "c-btn c-tile-select__cta" }, footer);
        }
        return null;
    };
    const renderIfError = () => {
        if (!errorMessage)
            return null;
        return typeof errorMessage === 'string' ? React.createElement(ErrorMessage, { id: tileSelectId }, errorMessage) : errorMessage;
    };
    function renderTitle() {
        return headingLevel ? (React.createElement(Heading, { level: headingLevel, className: "c-tile-select__title", id: `title-${tileSelectId}` }, title)) : (React.createElement("span", { className: "c-tile-select__title", id: `title-${tileSelectId}` }, title));
    }
    return (
    /**
     * Need to pass `[inert=""]` as React currently does not support HTMLElement.inert mapping.
     * @see https://github.com/facebook/react/pull/24730
     * @TODO: Remove once React supports direct `[inert]` usage.
     * @see BRON-11871
     */
    // @ts-ignore @TODO: Remove once React supports inerts, @see BRON-11871.
    React.createElement("div", { ...otherProps, "data-testid": testId, "aria-disabled": disabled, className: "c-tile-select", inert: disabled ? '' : null },
        React.createElement("div", { className: "c-tile-select__body" },
            React.createElement("label", { className: `c-${inputType} c-tile-select__label` },
                React.createElement("input", { className: `c-${inputType}__input c-tile-select__input`, type: inputType, id: tileSelectId, value: value, name: inputName, checked: checked, defaultChecked: defaultChecked, disabled: disabled, "aria-invalid": !!errorMessage || undefined, "aria-describedby": errorMessage ? `error-${tileSelectId}` : undefined, "aria-details": children ? `text-${tileSelectId}` : undefined, "aria-labelledby": `title-${tileSelectId}`, onClick: handleClick, onChange: handleChange, ...inputProps }),
                React.createElement("span", { className: `c-${inputType}__label` }),
                React.createElement("span", { className: "c-tile-select__body-content" },
                    icon && React.createElement(Icon, { className: iconClassNameList, name: icon }),
                    renderTitle(),
                    children && (React.createElement("span", { className: "c-tile-select__text", id: `text-${tileSelectId}` }, children)),
                    renderIfError(),
                    renderIfFooter())))));
}
